import CertificateIcon from '../../assets/icons/document-certificate.svg?react';
import ManualIcon from '../../assets/icons/document-manual.svg?react';
import SparePartsIcon from '../../assets/icons/document-spare-parts.svg?react';
import type { Machine } from '../../gql/generated';

/**
 * DocPortal of the same stage as this Pulse. Its hosts are `docs.<stage>.`
 * next to Pulse's `pulse.<stage>.`, and `docs.` without a stage in production.
 */
function docPortalOrigin(hostname: string): string {
  if (hostname.startsWith('pulse.qa.')) {
    return 'https://docs.qa.food-digital.tomra.com';
  }
  if (hostname.startsWith('pulse.dev.') || hostname === 'localhost') {
    return 'https://docs.dev.food-digital.tomra.com';
  }
  return 'https://docs.food-digital.tomra.com';
}

/**
 * The document kinds DocPortal holds for a machine. DocPortal searches by
 * serial only, so every row opens the same search; the rows tell a visitor
 * what they will find there.
 */
const DOCUMENT_KINDS = [
  { name: 'Manuals', icon: ManualIcon },
  { name: 'Spare parts guides', icon: SparePartsIcon },
  { name: 'Certificates', icon: CertificateIcon },
];

/**
 * The Documents tab. DocPortal holds the machine's manuals, spare part guides
 * and certificates, and finds them by serial number. The rows read as the
 * demo's document list, and each one opens DocPortal.
 */
export function MachineDocumentsLinkTab({ machine }: { machine: Machine }) {
  const serial = machine.serial;
  const href = serial
    ? `${docPortalOrigin(window.location.hostname)}/search?q=${encodeURIComponent(serial)}`
    : null;

  return (
    <div
      className='bg-surface-focus-support flex flex-col gap-3 px-4 pt-4 pb-10'
    >
      <h3 className='size-caption text-primary tracking-[0.08em] uppercase'>
        Tomra DocPortal
      </h3>
      {href ? (
        <ul className='bg-surface-primary overflow-hidden rounded-lg'>
          {DOCUMENT_KINDS.map(({ name, icon: Icon }) => (
            <li key={name}>
              <a
                href={href}
                target='_blank'
                rel='noreferrer'
                className='active:bg-surface-focus-support flex w-full px-4
                  text-left'
              >
                {/* The line between rows stops 16px short of the card edges,
                    so it sits inside the row's padding. */}
                <span
                  className='border-border-secondary flex min-w-0 flex-1
                    items-center gap-5 border-t py-4
                    [li:first-child_&]:border-t-0'
                >
                  <Icon aria-hidden className='h-6 w-6 shrink-0' />
                  <span className='flex min-w-0 flex-1 flex-col'>
                    <span className='size-p2 text-primary truncate font-medium'>
                      {name}
                    </span>
                    <span className='size-caption text-secondary truncate'>
                      Serial {serial}
                    </span>
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className='bg-surface-primary size-p2 text-secondary rounded-lg p-4'>
          This machine has no serial number, so DocPortal cannot find its
          documents.
        </p>
      )}
    </div>
  );
}
